import { Box, Typography, Card, CardActionArea, Radio, FormHelperText } from "@mui/material";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { EgyptIcon, OtherIcon } from "../../../../../assets/icons/MUI/PaymentIcons";
import { usePaymentAccounts } from "../../../../../api/queries/payment/paymentQueries";

import { Country } from "../types";
import { countrySchema } from "../schema";

const CountryStepFields = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { field } = useController({
    name: "country",
    control,
    defaultValue: "",
  });

  const handleOnCountrySelect = (method: Country) => field.onChange(method);

  const { userPaymentAccount, isUserPaymentAccountLoading, isUserPaymentAccountHasError } = usePaymentAccounts();

  const isPaypal = userPaymentAccount?.type === "paypal";
  const isWallet = userPaymentAccount?.type === "e_wallet";

  const countries = {
    egypt: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.egypt"),
      icon: <EgyptIcon />,
    },
    other: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.paypal"),
      icon: <OtherIcon />,
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", rowGap: "24px", padding: "0 24px" }}>
      <Typography
        component="h5"
        sx={{
          color: "var(--Gray-600, #475467)",
          fontFamily: "Montserrat",
          fontSize: "18px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "28px",
        }}
      >
        {t("pages.payment.overview.v2.receivingDetailsPopUp.selectWalletCountryDescription")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {countrySchema.options.map((option) => {
          if (isPaypal && option === "egypt") return null;
          if (isWallet && option === "other") return null;

          return (
            <Card
              key={option}
              sx={{
                flex: 1,
                border:
                  field.value === option
                    ? "1.5px solid var(--Green---100, #008B07)"
                    : "1.5px solid var(--Gray---100, #E7E7E7)",
                borderRadius: "12px",
                padding: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onClick={() => handleOnCountrySelect(option)}
            >
              <CardActionArea
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: "16px",
                  "& .MuiCardActionArea-focusHighlight": {
                    backgroundColor: "transparent !important",
                  },
                }}
                disableRipple
              >
                {countries[option].icon}
                <Typography
                  sx={{
                    color: "var(--Dark---1000, #111)",
                    fontFamily: "Montserrat",
                    fontSize: "18px",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "28px",
                  }}
                >
                  {countries[option].label}
                </Typography>
              </CardActionArea>
              <Radio value={option} checked={field.value === option} />
            </Card>
          );
        })}
      </Box>

      {errors.country && (
        <FormHelperText error>
          {t(`pages.payment.overview.v2.receivingDetailsPopUp.${errors.country.message}`)}
        </FormHelperText>
      )}
    </Box>
  );
};

export default CountryStepFields;
